import {
    type FC,
    useRef
} from "react";
import {
    type NativeSyntheticEvent,
    type NativeScrollEvent,
    type LayoutChangeEvent,
    ScrollView
} from "react-native";
import type ISidebarContentProps from "./type";
import stylesheet from "./stylesheet";
import {
    useSidebarRootContext
} from "../../context";

const SidebarContent: FC<ISidebarContentProps> = ({
    contentContainerStyle,
    onContentSizeChange,
    scrollEventThrottle,
    children,
    onLayout,
    onScroll,
    style,
    ...props
}) => {
    const root = useSidebarRootContext({
        part: "Sidebar.Content"
    });

    const metrics = useRef({
        contentHeight: 0,
        layoutHeight: 0,
        offset: 0
    });

    const syncScrollState = () => {
        root?.handleScrollState({
            isOverflow: metrics.current.contentHeight > metrics.current.layoutHeight,
            isScrolled: metrics.current.offset > 0
        });
    };

    const onContentLayout = (event: LayoutChangeEvent) => {
        metrics.current.layoutHeight = event.nativeEvent.layout.height;

        syncScrollState();

        onLayout?.(event);
    };

    const onContentResize = (width: number, height: number) => {
        metrics.current.contentHeight = height;

        syncScrollState();

        onContentSizeChange?.(width, height);
    };

    const onContentScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        metrics.current.offset = event.nativeEvent.contentOffset.y;

        syncScrollState();

        onScroll?.(event);
    };

    return <ScrollView
        {...props}
        scrollEventThrottle={scrollEventThrottle ?? 16}
        onContentSizeChange={onContentResize}
        onScroll={onContentScroll}
        onLayout={onContentLayout}
        contentContainerStyle={[
            stylesheet.content,
            contentContainerStyle
        ]}
        style={[
            style,
            stylesheet.container
        ]}
        aria-label={root?.title}
        role="navigation"
    >
        {children}
    </ScrollView>;
};
export default SidebarContent;
